/**
 * Everything that must be true before a view renders, and a NAMED screen for
 * every way it can fail.
 *
 * Boot resolves two things:
 *
 *   1. GET /admin-config.json (C1, served by the BFF) -- the runtime config,
 *      because nothing environment-specific is compiled into the bundle.
 *   2. GET /admin-api/me (D2) -- who this is, which tenant, which role, which
 *      features. Every gate in the shell reads this one record.
 *
 * WHICH screen each failure produces is decided by `chooseBootScreen` in
 * lib/bootState.ts, not here: the ordering rule (the Turnstile site key only
 * matters once `me` has answered 401) and the fault attribution (a config
 * STATUS answer is the deployment's fault, not the owner's network) are the
 * two things worth pinning, and pinning them in a pure test costs no DOM
 * suite. This file wires that answer to components and to the two refetches.
 *
 * The query options are exported as factories for the same reason: `retry:
 * false` on `me` is a policy statement (a 401 is not a failure to retry, it is
 * the unauthenticated state) and a factory can be asserted without a renderer.
 */
import { useCallback } from 'react';
import type { ReactNode } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { ADMIN_API, AUTH_API, adminFetch } from './lib/api';
import { chooseBootScreen } from './lib/bootState';
import { ROUTES } from './lib/router';
import { queryKeys } from './queries';
import { Blocking } from './components/Blocking';
import { Spinner } from './components/Spinner';
import { Login } from './views/Login';
import type { AdminConfig, MeResponse } from './lib/types';

/** staleTime Infinity: the config changes per DEPLOY, not per session. */
export function adminConfigQueryOptions() {
  return {
    queryKey: queryKeys.config,
    queryFn: () => adminFetch<AdminConfig>('/admin-config.json'),
    staleTime: Infinity,
  };
}

/**
 * retry false: a 401 here is not an error state, it is the unauthenticated
 * state. Retrying it is a loop against a session that no longer exists, and it
 * must not be thrown to an error boundary either.
 */
export function meQueryOptions() {
  return {
    queryKey: queryKeys.me,
    queryFn: () => ADMIN_API.me(),
    retry: false,
  };
}

export function useAdminConfig() {
  return useQuery(adminConfigQueryOptions());
}

export function useMe() {
  return useQuery(meQueryOptions());
}

/**
 * "Abmelden", in one place because two of its three steps are easy to forget,
 * and injectable because the one that matters most is invisible from the UI.
 *
 * queryClient.clear() is NOT optional: a stale `me` (or a cached staff list)
 * left in memory after a logout is the only way this SPA could show one user
 * another user's data. The reload then re-asks the server who this is, which
 * is also why a FAILED logout call still clears and reloads -- the alternative
 * is a shell that looks signed in and is not.
 */
export async function performLogout(
  queryClient: QueryClient,
  redirect: (path: string) => void,
  logout: () => Promise<unknown> = () => AUTH_API.logout(),
): Promise<void> {
  try {
    await logout();
  } catch {
    // Reported by the reload, not by a toast on a screen that is going away.
  }
  queryClient.clear();
  redirect(ROUTES.start);
}

export function useLogout(): () => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(
    () => performLogout(queryClient, (path) => window.location.assign(path)),
    [queryClient],
  );
}

export function Boot({ children }: { children: (me: MeResponse) => ReactNode }) {
  const config = useAdminConfig();
  const me = useMe();

  const screen = chooseBootScreen(config, me);
  switch (screen.view) {
    case 'spinner':
      return <Spinner />;
    case 'blocking': {
      const retry = screen.retry;
      const onRetry =
        retry === 'config' ? () => void config.refetch()
        : retry === 'me' ? () => void me.refetch()
        : undefined;
      return <Blocking kind={screen.kind} onRetry={onRetry} />;
    }
    case 'login':
      return <Login siteKey={screen.siteKey} onSignedIn={() => void me.refetch()} />;
    case 'children':
      break;
  }

  // 'children' is returned only for a settled, successful `me`; the guard is
  // what lets the success type narrow without a cast.
  if (!me.isSuccess) return <Spinner />;
  return <>{children(me.data)}</>;
}
